<template>
  <VList
    bg-color="backgroundDiv"
    class="portal-update-list add-processing"
  >
    <SLoadingOverlay :loading="pending" />
    <VListItem v-if="!updates.length" class="portal-update-list__item py-2 mb-4 px-6" style="min-height:500px">
      <VContainer class="text-center">
        {{ $t('demo.label.noUpdates') }}
      </VContainer>
    </VListItem>
    <VListItem
      v-for="(update, index) in updates"
      :key="index"
      :data-testid="`portal-update-item-${index}`"
      class="portal-update-list__item py-2 mb-4 px-6"
    >
      <VRow no-gutters class="portal-update-list__item-row">
        <VCol :data-testid="`portal-update-image-column-${index}`" class="portal-update-list__image-column py-0" cols="auto">
          <SImage
            class="portal-update-list__image"
            height="80"
            :src="update.image"
          />
        </VCol>
        <VCol class="portal-update-list__text-column">
          <VRow no-gutters class="portal-update-list__content-row pb-0">
            <p class="portal-update-list__title text-uppercase font-weight-medium mb-1 font12">
              {{ update.title }}
            </p>
            <p class="portal-update-list__summary ellipsis mb-1 font12 text-greyedOut">
              {{ update.summary }}
            </p>
          </VRow>
          <VRow no-gutters class="portal-update-list__meta-row pt-0 align-baseline">
            <VCol class="portal-update-list__date-column pt-0 pl-0">
              <p class="portal-update-list__date mb-1 font12 font-weight-medium text-greyedOut">
                {{ update.date }}
              </p>
            </VCol>
            <VCol class="portal-update-list__actions-column pt-0 text-right">
              <SBtn
                link-small
                :to="resolveUpdateUrl(update.url)"
                :data-testid="`portal-update-read-more-${index}`"
                style="text-transform: none;"
                class="portal-update-list__read-more font12"
                @click="emits('click')"
              >
                {{ $t('link.readMore') }}
              </SBtn>
            </VCol>
          </VRow>
        </VCol>
      </VRow>
    </VListItem>
    <VRow v-if="updates.length" no-gutters class="py-0 px-3">
      <VCol class="text-right">
        <SBtn
          link-small
          data-testid="portal-update-view-all"
          class="portal-update-list__view-all text-right font12"
          :to="currentUpdatesPath"
          style="text-transform: none;"
          @click="emits('click')"
        >
          {{ $t('link.viewAllUpdates') }}
        </SBtn>
      </VCol>
    </VRow>
  </VList>
</template>
<script setup lang="ts">
import { useEventStore } from '~/stores/events'
import { PORTAL_BUSINESS_RESOURCES, PORTAL_CURRENT_UPDATES, PORTAL_ROOT } from '~/utils/portalRoutes'
const eventStore = useEventStore()

const currentUpdatesPath = PORTAL_CURRENT_UPDATES

const resolveUpdateUrl = (url: string) => {
  return url.replace(`${PORTAL_BUSINESS_RESOURCES}/`, `${PORTAL_ROOT}/`)
}

const updates = computed(() => eventStore.updates)

// The handler already swallows a failed queryUpdates itself (nuxt2 parity --
// AppInsights logs it in the api.ts onResponseError hook) so the user just
// sees the existing "no updates" empty state below. useLazyAsyncData never
// needs to surface an error here, but this must not be the page-fatal
// composable regardless -- see AB#62911.
const { pending } = useLazyAsyncData('fetch-updates', async () => {
  try {
    await eventStore.queryUpdates()
  } catch {
    // appInsights trackTrace in api.ts on onResponseError methodd
    // user will sees "no updates available" message
  }
  return true
})

const emits = defineEmits<{
  (e: 'click'): void
}>()
</script>

<style lang="scss" scoped>
@use '~/assets/scss/settings';
@use 'sass:map';

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.portal-update-list {
  padding-top: 8px;
  padding-bottom: 8px;
  height: auto;
  overflow: visible;
}

.portal-update-list__image {
  width: 80px;
  max-width: 80px;
}

.portal-update-list__item-row {
  margin: -12px;
}

.portal-update-list__content-row {
  margin: -12px;
}

.portal-update-list__item-row > :deep(.v-col),
.portal-update-list__item-row > :deep([class*='v-col-']) {
  padding-left: 12px;
  padding-right: 12px;
}

.portal-update-list__item-row > :deep(.v-col) {
  padding-top: 12px;
  padding-bottom: 12px;
}

.portal-update-list__meta-row {
  margin: 12px -12px -12px;
  min-height: 0;
}

.portal-update-list__meta-row > :deep(.v-col),
.portal-update-list__meta-row > :deep([class*='v-col-']) {
  padding-top: 0;
  padding-right: 12px;
  padding-bottom: 12px;
  padding-left: 12px;
}

.portal-update-list__meta-row > :deep(.v-col:first-child),
.portal-update-list__meta-row > :deep([class*='v-col-']:first-child) {
  padding-left: 0;
}

.portal-update-list__meta-row > :deep(.v-col:last-child),
.portal-update-list__meta-row > :deep([class*='v-col-']:last-child) {
  line-height: 21px;
}

.portal-update-list__title {
  color: rgb(var(--v-theme-on-surface));
}

.portal-update-list__summary,
.portal-update-list__date {
  color: rgb(var(--v-theme-greyedOut));
}

.portal-update-list__item {
  background-color: transparent;
}

// These Vuetify internals are part of the portal update-card composition
// itself: flush list-item content, no text-variant overlay, and inline link
// content for the card footer actions. Keep them local until the same drift is
// proven on non-portal VList/SBtn surfaces.
.portal-update-list__item :deep(.v-list-item__content) {
  padding: 0;
  display: flex;
  align-items: center;
  overflow: visible;
}

.portal-update-list__item :deep(.v-list-item__overlay) {
  display: none;
}

.portal-update-list__actions-column {
  line-height: 21px;
}

.portal-update-list__read-more,
.portal-update-list__view-all {
  color: rgb(var(--v-theme-on-surface));
  line-height: 18px;
  min-width: 0;
  padding: 0;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    min-height: 14px;
    height: 14px;
  }
}

.portal-update-list__read-more {
  margin-top: 1px;
}

.portal-update-list__read-more :deep(.v-btn__content),
.portal-update-list__view-all :deep(.v-btn__content) {
  display: inline;
}

.portal-update-list__view-all {
  letter-spacing: 1.07138px;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    height: 14px;
  }
}
</style>
